import React, { useEffect, useState, useCallback, useRef } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { TextField } from "@mui/material";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaSearch, FaHome, FaMapMarkerAlt, FaExternalLinkAlt, FaPen,
  FaChevronLeft, FaChevronRight, FaRobot, FaCheckCircle, FaExclamationTriangle,
} from "react-icons/fa";
import { useViewScope } from "../contexts/ViewScopeContext";
import PropertyGuidanceModal from "../components/PropertyGuidanceModal";

function formatMoney(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2).replace(/\.00$/, "")}M`;
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${n.toLocaleString()}`;
}

// The three modes for the global Property Updates campaign.
const MODES = [
  { key: "on", label: "On", help: "Send property updates for every eligible contact." },
  { key: "guided", label: "Guided", help: "Only send for properties where you've set a Low / Mid / High range below." },
  { key: "off", label: "Off", help: "Pause all property update emails and texts." },
];

function PropertyCard({ property, isMobile, onEdit }) {
  const address =
    property.property_address ||
    [property.suburb, property.state].filter(Boolean).join(", ") ||
    "Property";
  const hasRange =
    property.value_low != null && property.value_mid != null && property.value_high != null;

  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      border: `1px solid ${property.has_guidance ? "#99f6e4" : "#e5e7eb"}`,
      padding: isMobile ? "14px 16px" : "16px 20px",
      display: "flex", flexDirection: isMobile ? "column" : "row",
      gap: isMobile ? 12 : 16,
      alignItems: isMobile ? "stretch" : "center",
      transition: "box-shadow 0.15s, border-color 0.15s",
    }}
      onMouseEnter={(e) => { if (!isMobile) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.06)"; }}
      onMouseLeave={(e) => { if (!isMobile) e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Left: address + link */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{
            fontWeight: 600, fontSize: 15, color: "#111827",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {address}
          </span>
          {property.has_guidance ? (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", borderRadius: 999, background: "#f0fdfa",
              color: "#0f766e", fontSize: 10, fontWeight: 700, flexShrink: 0,
            }}>
              <FaCheckCircle style={{ fontSize: 9 }} /> Guidance set
            </span>
          ) : (
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "2px 8px", borderRadius: 999, background: "#f3f4f6",
              color: "#9ca3af", fontSize: 10, fontWeight: 600, flexShrink: 0,
            }}>
              No guidance yet
            </span>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 16px", fontSize: 13, color: "#6b7280" }}>
          {(property.suburb || property.state) && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FaMapMarkerAlt style={{ fontSize: 11, color: "#9ca3af" }} />
              {[property.suburb, property.state].filter(Boolean).join(", ")}
            </span>
          )}
          {property.branded_url && (
            <a
              href={property.branded_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "#0d9488", fontWeight: 600, textDecoration: "none" }}
            >
              <FaExternalLinkAlt style={{ fontSize: 10 }} /> Property page
            </a>
          )}
        </div>
        {property.notes && (
          <div style={{
            marginTop: 8, fontSize: 12.5, color: "#6b7280", lineHeight: 1.5,
            overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
            WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
          }}>
            {property.notes}
          </div>
        )}
      </div>

      {/* Middle: Low / Mid / High display (read-only) */}
      <div style={{
        display: "flex", gap: 8, flexShrink: 0,
        ...(isMobile ? { borderTop: "1px solid #f3f4f6", paddingTop: 12 } : {}),
      }}>
        {[
          { label: "Low", val: property.value_low },
          { label: "Mid", val: property.value_mid },
          { label: "High", val: property.value_high },
        ].map((c) => (
          <div key={c.label} style={{
            minWidth: 64, textAlign: "center",
            background: hasRange ? "#f8fafc" : "#fafafa",
            border: "1px solid #f1f5f9", borderRadius: 10, padding: "6px 10px",
          }}>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{c.label}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.val != null ? "#111827" : "#cbd5e1", marginTop: 2 }}>
              {formatMoney(c.val)}
            </div>
          </div>
        ))}
      </div>

      {/* Right: Edit */}
      <button
        onClick={() => onEdit(property)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          padding: "8px 16px", borderRadius: 9,
          background: property.has_guidance ? "#fff" : "#0d9488",
          color: property.has_guidance ? "#0f766e" : "#fff",
          border: property.has_guidance ? "1px solid #99f6e4" : "none",
          fontSize: 13, fontWeight: 600, cursor: "pointer", flexShrink: 0,
          ...(isMobile ? { width: "100%" } : {}),
        }}
      >
        <FaPen style={{ fontSize: 11 }} /> {property.has_guidance ? "Edit" : "Add guidance"}
      </button>
    </div>
  );
}

export default function Properties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;
  const { viewScope, agentFilter } = useViewScope();

  const [mode, setMode] = useState("on");
  const [modeSaving, setModeSaving] = useState(false);
  const [editProperty, setEditProperty] = useState(null);

  useEffect(() => {
    function handleResize() { setIsMobile(window.innerWidth < 768); }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const searchDebounceRef = useRef(null);

  const fetchProperties = useCallback(async (searchTerm, pg) => {
    setLoading(true);
    try {
      const body = { view_scope: viewScope, agent_filter: agentFilter, page: pg || 1, page_size: pageSize };
      if (searchTerm) body.search = searchTerm;
      const res = await handleTokenAndPostRequest(`${window.api_host}/agent-list-properties`, body);
      const data = res.data?.properties || [];
      setProperties(data.map((p, i) => ({ ...p, _rowId: `${p.property_id}-${i}` })));
      setTotalCount(res.data?.total_count ?? data.length);
      if (res.data?.property_updates_mode) setMode(res.data.property_updates_mode);
    } catch (e) {
      console.error("Failed to fetch properties:", e);
      setProperties([]);
      setTotalCount(0);
    }
    setLoading(false);
  }, [viewScope, agentFilter]);

  useEffect(() => {
    fetchProperties(search, page);
  }, [fetchProperties, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search — resets to page 1.
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(1);
      fetchProperties(search, 1);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetMode = async (newMode) => {
    if (newMode === mode || modeSaving) return;
    const prev = mode;
    setMode(newMode); // optimistic
    setModeSaving(true);
    try {
      await handleTokenAndPostRequest(`${window.api_host}/agent-set-property-updates-mode`, { mode: newMode });
    } catch (e) {
      console.error("Failed to set mode:", e);
      setMode(prev); // rollback
    } finally {
      setModeSaving(false);
    }
  };

  // Merge a saved guidance back into the visible list without a refetch.
  const handleSaved = (payload) => {
    if (!payload?.property_id) return;
    setProperties((prev) => prev.map((p) =>
      p.property_id === payload.property_id ? { ...p, ...payload } : p
    ));
    setEditProperty((cur) => (cur && cur.property_id === payload.property_id ? { ...cur, ...payload } : cur));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalCount);
  const activeModeHelp = MODES.find((m) => m.key === mode)?.help || "";

  return (
    <div style={{
      padding: isMobile ? "0 16px" : "0 32px",
      flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
      background: "#f6f8fa", boxSizing: "border-box", width: "100%", overflowX: "hidden", overflowY: "auto",
    }}>
      <div style={{ maxWidth: 1200, marginLeft: "auto", marginRight: "auto", width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>

        <div style={{
          position: isMobile ? "static" : "sticky", top: 0, zIndex: 10, background: "#f6f8fa",
          paddingTop: isMobile ? 72 : 32, paddingBottom: 8,
          marginLeft: isMobile ? -16 : -32, marginRight: isMobile ? -16 : -32,
          paddingLeft: isMobile ? 16 : 32, paddingRight: isMobile ? 16 : 32,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 700, margin: 0, color: "#1f2937" }}>Properties</h1>
            {!loading && (
              <span style={{ fontSize: 14, color: "#6b7280", fontWeight: 400 }}>
                {totalCount.toLocaleString()} total
              </span>
            )}
          </div>
          <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 14, marginTop: 2, maxWidth: 680, lineHeight: 1.5 }}>
            Give the AI a Low / Mid / High price range and notes for each property. Your
            guidance overrides the AI's estimate and personalises what customers see.
          </p>

          {/* Property Updates mode */}
          <div style={{
            display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12,
            background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
            padding: isMobile ? "12px 14px" : "12px 16px", marginBottom: 14,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <FaRobot style={{ color: "#0d9488", fontSize: 14 }} />
              <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Property Updates</span>
            </div>
            <div style={{ display: "inline-flex", borderRadius: 999, border: "1px solid #e5e7eb", background: "#f9fafb", padding: 2, gap: 2 }}>
              {MODES.map((m) => {
                const active = mode === m.key;
                return (
                  <button
                    key={m.key}
                    onClick={() => handleSetMode(m.key)}
                    disabled={modeSaving}
                    style={{
                      padding: "5px 14px", borderRadius: 999, border: "none",
                      background: active ? (m.key === "off" ? "#1f2937" : "#0d9488") : "transparent",
                      color: active ? "#fff" : "#6b7280",
                      fontSize: 12.5, fontWeight: 600, cursor: modeSaving ? "wait" : "pointer",
                      transition: "background 0.12s ease",
                    }}
                  >
                    {m.label}
                  </button>
                );
              })}
            </div>
            <span style={{
              fontSize: 12, color: mode === "guided" ? "#0f766e" : "#9ca3af",
              display: "inline-flex", alignItems: "center", gap: 6, lineHeight: 1.4,
            }}>
              {mode === "guided" && <FaExclamationTriangle style={{ fontSize: 11, flexShrink: 0 }} />}
              {activeModeHelp}
            </span>
          </div>

          {/* Search */}
          <div style={{ marginBottom: isMobile ? 10 : 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px", maxWidth: 400 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search by address or suburb..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <FaSearch style={{ color: "#9ca3af", marginRight: 8, fontSize: 14 }} />,
                }}
              />
            </div>
            {!isMobile && !loading && totalCount > 0 && (
              <span style={{ fontSize: 13, color: "#9ca3af" }}>
                Showing {showingFrom}–{showingTo} of {totalCount.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} style={{
                background: "#fff", borderRadius: 12, padding: isMobile ? "14px 16px" : "16px 20px",
                border: "1px solid #e5e7eb",
                display: "flex", flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center", gap: isMobile ? 10 : 16,
              }}>
                <div style={{ flex: 1 }}>
                  <Skeleton width={180 + Math.random() * 80} height={16} />
                  <div style={{ marginTop: 6 }}><Skeleton width={120} height={13} /></div>
                </div>
                <Skeleton width={210} height={42} borderRadius={10} />
                <Skeleton width={110} height={36} borderRadius={9} />
              </div>
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "60px 20px", color: "#9ca3af",
            background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb",
          }}>
            <FaHome style={{ fontSize: 28, marginBottom: 12, color: "#d1d5db" }} />
            <div style={{ fontSize: 16, fontWeight: 500 }}>No properties yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              Properties appear here from your contacts' addresses and from the Property Searches your leads run.
            </div>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {properties.map((p) => (
                <PropertyCard
                  key={p._rowId}
                  property={p}
                  isMobile={isMobile}
                  onEdit={setEditProperty}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
                padding: "16px 0", marginTop: 8,
              }}>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb",
                    background: page <= 1 ? "#f9fafb" : "#fff", color: page <= 1 ? "#d1d5db" : "#374151",
                    fontSize: 13, fontWeight: 500, cursor: page <= 1 ? "default" : "pointer",
                  }}
                >
                  <FaChevronLeft style={{ fontSize: 10 }} /> Previous
                </button>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Page {page} of {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 4,
                    padding: "8px 14px", borderRadius: 8, border: "1px solid #e5e7eb",
                    background: page >= totalPages ? "#f9fafb" : "#fff", color: page >= totalPages ? "#d1d5db" : "#374151",
                    fontSize: 13, fontWeight: 500, cursor: page >= totalPages ? "default" : "pointer",
                  }}
                >
                  Next <FaChevronRight style={{ fontSize: 10 }} />
                </button>
              </div>
            )}
          </>
        )}

        {editProperty && (
          <PropertyGuidanceModal
            property={editProperty}
            onClose={() => setEditProperty(null)}
            onSaved={handleSaved}
          />
        )}
      </div>
    </div>
  );
}
